import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Shield,
  TrendingUp,
  Users,
  Award,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Hero() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const navigate = useNavigate();

  const heroSlides = [
    {
      id: 1,
      title: "Join the Elite Insurance Network",
      subtitle:
        "Become a TripSecure Agent and unlock unlimited earning potential",
      description:
        "Start your journey as a trusted insurance professional with comprehensive training, cutting-edge tools, and industry-leading commissions.",
      colorScheme: "emerald",
      backgroundImage:
        "https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2071&q=80",
      cta: "Become an Agent",
      ctaLink: "/signin",
    },
    {
      id: 2,
      title: "Earn While You Serve",
      subtitle:
        "Transform lives through protection, build wealth through service",
      description:
        "Help families secure their future while building a sustainable income stream with our competitive commission structure.",
      colorScheme: "blue",
      backgroundImage:
        "https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      cta: "Get Started",
      ctaLink: "/signin",
    },
    {
      id: 3,
      title: "Professional Growth & Support",
      subtitle: "Access world-class training and continuous development",
      description:
        "From onboarding to advanced sales techniques, we provide everything you need to succeed in the insurance industry.",
      colorScheme: "purple",
      backgroundImage:
        "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2071&q=80",
      cta: "Get Started",
      ctaLink: "/signin",
    },
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, heroSlides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + heroSlides.length) % heroSlides.length
    );
    setIsAutoPlaying(false);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
  };

  const currentSlideData = heroSlides[currentSlide];

  // Helper function to get color classes based on color scheme
  const getColorClasses = (colorScheme) => {
    switch (colorScheme) {
      case "emerald":
        return {
          bg: "bg-gradient-to-br from-emerald-600 to-green-700",
          text: "text-emerald-100",
          textLight: "text-emerald-50",
          button: "bg-white text-emerald-700 hover:bg-emerald-50",
          blur: "bg-emerald-500/20",
          tint: "from-emerald-500/25 via-emerald-500/20 to-transparent",
        };
      case "blue":
        return {
          bg: "bg-gradient-to-br from-blue-600 to-indigo-700",
          text: "text-blue-100",
          textLight: "text-blue-50",
          button: "bg-white text-blue-700 hover:bg-blue-50",
          blur: "bg-blue-500/20",
          tint: "from-blue-500/25 via-blue-500/20 to-transparent",
        };
      case "purple":
        return {
          bg: "bg-gradient-to-br from-purple-600 to-indigo-700",
          text: "text-purple-100",
          textLight: "text-purple-50",
          button: "bg-white text-purple-700 hover:bg-purple-50",
          blur: "bg-purple-500/20",
          tint: "from-purple-500/25 via-purple-500/20 to-transparent",
        };
      default:
        return {
          bg: "bg-gradient-to-br from-emerald-600 to-green-700",
          text: "text-emerald-100",
          textLight: "text-emerald-50",
          button: "bg-white text-emerald-700 hover:bg-emerald-50",
          blur: "bg-emerald-500/20",
          tint: "from-emerald-500/25 via-emerald-500/20 to-transparent",
        };
    }
  };

  const colors = getColorClasses(currentSlideData.colorScheme);

  return (
    <section className="overflow-hidden relative min-h-screen">
      {/* Background Image */}
      <motion.div
        key={currentSlide}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="absolute inset-0"
      >
        <img
          src={currentSlideData.backgroundImage}
          alt="Hero Background"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/50 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/50 to-black/30" />
      </motion.div>

      {/* Content */}
      <div className="flex relative z-10 items-center pt-20 min-h-screen">
        <div className="px-4 mx-auto w-full max-w-7xl sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center">
            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-center lg:text-left"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="inline-flex items-center px-4 py-2 mb-4 text-sm font-medium text-white rounded-full backdrop-blur-sm bg-white/10"
              >
                <Shield size={16} className="mr-2" />
                Trusted by 10,000+ Agents
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                  className="mb-6 text-lg font-bold leading-tight text-white sm:text-xl lg:text-4xl"
              >
                {currentSlideData.title}
              </motion.h1>

              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="mb-6 text-xl font-medium text-emerald-200 sm:text-xl"
              >
                {currentSlideData.subtitle}
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.0 }}
                className="mb-8 text-lg text-emerald-100 sm:text-lg"
              >
                {currentSlideData.description}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.2 }}
                className="flex flex-col gap-4 sm:flex-row sm:justify-center lg:justify-start"
              >
                <button
                  className={`rounded-full px-8 py-4 font-semibold transition-all duration-300 hover:scale-105 ${colors.button}`}
                  onClick={() => navigate(currentSlideData.ctaLink)}
                >
                  {currentSlideData.cta}
                </button>
                <button className="px-8 py-4 font-semibold text-white rounded-full border-2 transition-all duration-300 border-white/30 hover:bg-white/10 hover:border-white/50">
                  Contact Us
                </button>
              </motion.div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.4 }}
                className="grid grid-cols-3 gap-6 mt-12 text-center lg:text-left"
              >
                <div>
                  <div className="text-2xl font-bold text-white">₵50K+</div>
                  <div className="text-sm text-emerald-200">
                    Avg. Monthly Earnings
                  </div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">95%</div>
                  <div className="text-sm text-emerald-200">Success Rate</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">24/7</div>
                  <div className="text-sm text-emerald-200">Support</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Visual Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="hidden relative lg:block"
            >
              <div className="grid grid-cols-1 gap-6 mx-auto max-w-md">
                {/* Main Card */}
                <div className="overflow-hidden relative p-8 rounded-2xl border backdrop-blur-sm bg-white/10 border-white/20">
                  <div className="flex justify-center items-center space-x-4">
                    <div className="flex justify-center items-center w-16 h-16 bg-emerald-500 rounded-full">
                      <TrendingUp size={32} className="text-white" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-xl font-semibold text-white">
                        Commission Dashboard
                      </h3>
                      <p className="text-emerald-200">
                        Track your earnings in real-time
                      </p>
                    </div>
                  </div>
                </div>

                {/* Side Cards in a row */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Left Card */}
                  <div className="overflow-hidden p-6 rounded-2xl border backdrop-blur-sm bg-white/10 border-white/20">
                    <div className="flex flex-col items-center space-y-3 text-center">
                      <div className="flex justify-center items-center w-12 h-12 bg-purple-500 rounded-full">
                        <Award size={24} className="text-white" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-white">
                          Performance Rewards
                        </h4>
                        <p className="text-xs text-purple-200">
                          Earn bonuses & recognition
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Right Card */}
                  <div className="overflow-hidden p-6 rounded-2xl border backdrop-blur-sm bg-white/10 border-white/20">
                    <div className="flex flex-col items-center space-y-3 text-center">
                      <div className="flex justify-center items-center w-12 h-12 bg-blue-500 rounded-full">
                        <Users size={24} className="text-white" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-white">
                          Client Management
                        </h4>
                        <p className="text-xs text-blue-200">
                          Organize & grow your client base
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="flex absolute bottom-8 left-1/2 z-20 space-x-2 -translate-x-1/2">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-3 w-3 rounded-full transition-all duration-300 ${
              index === currentSlide
                ? "w-8 bg-white"
                : "bg-white/50 hover:bg-white/75"
            }`}
          />
        ))}
      </div>

      {/* Arrow Navigation */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 z-20 p-3 text-white rounded-full backdrop-blur-sm transition-all duration-300 -translate-y-1/2 bg-white/10 hover:bg-white/20"
      >
        <ChevronLeft size={24} />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 z-20 p-3 text-white rounded-full backdrop-blur-sm transition-all duration-300 -translate-y-1/2 bg-white/10 hover:bg-white/20"
      >
        <ChevronRight size={24} />
      </button>
    </section>
  );
}
